import React from 'react';
import { useData, dataFromDataset } from './DataProviderCache';
import * as LocationManager from '../domain/location-manager';
import { Data, isDataLoaded, makeLoadedData, ExtractDataType } from '../../types/data-providers';
import { useAuthHelper } from '../../components/AuthContextProvider';
import { SiteInfo } from '../../types';

type ContextState = Data<{
    id: string;
    eventCount: number | undefined;
    siteCount: number | undefined;
    sites: Data<SiteInfo[]> | undefined;
}>;
type LocationData = ExtractDataType<ContextState>;

const Context = React.createContext<ContextState | null>(null);

function useLocationData(): ContextState {
    const contextState = React.useContext(Context);
    if (contextState === null) {
        throw new Error('useLocationData must be used within a LocationDataProvider');
    }
    return contextState;
}

const LocationDataProvider: React.FC<{ locationId: string }> = (props) => {
    const authHelper = useAuthHelper();
    const { dataset } = useData({
        cacheKey: 'location-data',
        id: props.locationId,
        dataFn: React.useCallback(
            async () =>
                await LocationManager.getLocationCardInfo(authHelper, { locationId: props.locationId }),
            [authHelper, props.locationId]
        ),
    });
    const LocationCardData = dataFromDataset(dataset);

    const value = React.useMemo(
        () =>
            makeLoadedData<LocationData>({
                id: props.locationId,
                eventCount: isDataLoaded(LocationCardData) ? LocationCardData.value.eventCount : undefined,
                siteCount:
                    isDataLoaded(LocationCardData) && isDataLoaded(LocationCardData.value.sites)
                        ? LocationCardData.value.sites.value.length
                        : 0,
                sites:
                    isDataLoaded(LocationCardData) && isDataLoaded(LocationCardData.value.sites)
                        ? LocationCardData.value.sites
                        : undefined,
            }),
        [LocationCardData, props.locationId]
    );

    return <Context.Provider value={value}>{props.children}</Context.Provider>;
};

const TestLocationDataProvider: React.FC<{ value: ContextState }> = (props) => (
    <Context.Provider value={props.value}>{props.children}</Context.Provider>
);

export { LocationDataProvider, TestLocationDataProvider, useLocationData };
